import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash, CurrencyDollar, Buildings, CalendarBlank, Note, PencilSimple } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";
import { FileVault } from "@/components/FileVault";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtMoney } from "@/lib/poFormat";

const today = () => new Date().toISOString().slice(0, 10);
const day = (iso) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const STATUS_TONE = {
  Paid: "border-emerald-500/40 text-emerald-300",
  Open: "border-amber-500/40 text-amber-300",
};

const Field = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-2">
    {Icon && <Icon size={15} weight="bold" className="mt-0.5 shrink-0 text-zinc-500" />}
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-200">{children || "—"}</div>
    </div>
  </div>
);

export default function BillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "Check", reference: "", note: "", at: today() });
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({ supplier_name: "", title: "", amount: "", due_date: "", notes: "" });

  const openEdit = () => {
    setEdit({
      supplier_name: bill.supplier_name || "",
      title: bill.title || "",
      amount: String(Math.abs(bill.amount) || ""),
      due_date: bill.due_date || "",
      notes: bill.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const amount = Number(edit.amount);
    if (!(amount > 0)) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.put(`/payables/bill/${id}`, {
        supplier_name: edit.supplier_name,
        title: edit.title,
        amount,
        due_date: edit.due_date,
        notes: edit.notes,
      });
      toast.success("Bill updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update bill");
    }
    setBusy(false);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/payables/bill/${id}`);
      setBill(data);
      setPay((cur) => ({ ...cur, amount: data.balance > 0 ? String(data.balance.toFixed(2)) : "" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bill not found");
      navigate(-1);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const recordPayment = async () => {
    const amount = Number(pay.amount);
    if (!(amount > 0)) return toast.error("Enter an amount");
    setBusy(true);
    try {
      await api.post(`/payables/bill/${id}/payments`, {
        amount,
        method: pay.method,
        reference: pay.reference,
        note: pay.note,
        at: pay.at ? new Date(`${pay.at}T12:00:00`).toISOString() : "",
      });
      toast.success("Payment recorded");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not record payment");
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm("Delete this bill? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/payables/bill/${id}`);
      toast.success("Bill deleted");
      navigate(-1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete bill");
      setBusy(false);
    }
  };

  if (!bill) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="bill-detail-page">
      <AppHeader />
      <div className="mx-auto max-w-3xl px-4 py-6">
        <button
          data-testid="bill-back-btn"
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono-data text-sm font-bold text-indigo-300" data-testid="bill-code">{bill.code}</span>
                <span
                  data-testid="bill-status"
                  className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${STATUS_TONE[bill.status] || "border-zinc-700 text-zinc-300"}`}
                >
                  {bill.status}
                </span>
                {bill.is_vendor_credit && (
                  <span className="rounded-full border border-sky-500/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-sky-300">Vendor credit</span>
                )}
              </div>
              <h1 className="mt-1 text-xl font-bold text-zinc-100" data-testid="bill-title">{bill.title}</h1>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                data-testid="bill-edit-btn"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={openEdit}
                className="h-8 border-zinc-700 text-[10px] font-bold uppercase tracking-widest text-zinc-200 hover:bg-zinc-800"
              >
                <PencilSimple size={13} weight="bold" className="mr-1" /> Edit
              </Button>
              <Button
                data-testid="bill-delete-btn"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={remove}
                className="h-8 border-red-500/40 text-[10px] font-bold uppercase tracking-widest text-red-300 hover:bg-red-500/10"
              >
                <Trash size={13} weight="bold" className="mr-1" /> Delete
              </Button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field icon={Buildings} label="Supplier">{bill.supplier_name}</Field>
            <Field icon={CalendarBlank} label="Issued">{day(bill.issued_at)}</Field>
            <Field icon={CalendarBlank} label="Due">{day(bill.due_date)}</Field>
          </div>
          {bill.notes && (
            <div className="mt-4">
              <Field icon={Note} label="Notes">{bill.notes}</Field>
            </div>
          )}

          <div className="mt-5 grid grid-cols-3 gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Amount</div>
              <div className="font-mono-data text-lg font-bold" data-testid="bill-amount">{fmtMoney(bill.amount)}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Paid</div>
              <div className="font-mono-data text-lg font-bold text-emerald-300" data-testid="bill-paid">{fmtMoney(bill.paid)}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Balance due</div>
              <div className={`font-mono-data text-lg font-bold ${bill.balance > 0.009 ? "text-amber-300" : "text-zinc-500"}`} data-testid="bill-balance">{fmtMoney(bill.balance)}</div>
            </div>
          </div>
        </div>

        {!bill.is_vendor_credit && bill.balance > 0.009 && (
          <div className="mt-4 rounded-xl border border-zinc-800 bg-zinc-900 p-5" data-testid="bill-pay-panel">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              <CurrencyDollar size={15} weight="bold" className="text-emerald-400" /> Record a payment
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Amount</div>
                <Input data-testid="bill-pay-amount" type="number" min="0" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 h-9 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Method</div>
                <Input data-testid="bill-pay-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="mt-1 h-9 border-zinc-800 bg-zinc-950 text-xs" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Reference</div>
                <Input data-testid="bill-pay-reference" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="mt-1 h-9 border-zinc-800 bg-zinc-950 text-xs" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Date</div>
                <DateField testid="bill-pay-date" value={pay.at} onChange={(v) => setPay({ ...pay, at: v })} className="mt-1" />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button data-testid="bill-pay-apply" disabled={busy || !(Number(pay.amount) > 0)} onClick={recordPayment} className="bg-emerald-500 hover:bg-emerald-400">Record payment</Button>
            </div>
          </div>
        )}

        <div className="mt-4 rounded-xl border border-zinc-800 bg-zinc-900 p-5" data-testid="bill-payments">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Payment history</div>
          {(!bill.payments || bill.payments.length === 0) ? (
            <p className="mt-3 text-sm text-zinc-500">No payments recorded yet.</p>
          ) : (
            <table className="mt-3 w-full text-sm">
              <tbody>
                {bill.payments.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-800/60 last:border-0" data-testid={`bill-payment-${p.id}`}>
                    <td className="py-2 text-xs text-zinc-400">{day(p.at || p.recorded_at)}</td>
                    <td className="py-2 text-xs text-zinc-300">{p.method || "—"}</td>
                    <td className="py-2 text-xs text-zinc-500">{p.reference || p.note || ""}</td>
                    <td className="py-2 text-right font-mono-data text-xs text-emerald-300">{fmtMoney(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-4">
          <FileVault entity="bills" entityId={id} title="Attached paperwork" />
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="bill-edit-dialog" className="border-zinc-800 bg-zinc-900 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Edit bill</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Fix the supplier, amount, due date or notes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Supplier</div>
              <Input data-testid="bill-edit-supplier" value={edit.supplier_name} onChange={(e) => setEdit({ ...edit, supplier_name: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Title</div>
              <Input data-testid="bill-edit-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Amount</div>
                <Input data-testid="bill-edit-amount" type="number" min="0" step="0.01" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Due date</div>
                <DateField testid="bill-edit-due" value={edit.due_date} onChange={(v) => setEdit({ ...edit, due_date: v })} className="mt-1" placeholder="Optional" />
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Notes</div>
              <Textarea data-testid="bill-edit-notes" rows={2} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-950" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="bill-edit-save" disabled={busy} onClick={saveEdit} className="bg-indigo-500 hover:bg-indigo-400">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
